
import { mapActions } from "vuex"
import mixins from "vue-typed-mixins"
import socialsMixin from "@/mixins/socials"
import { DeletionReason, DeletionRequestVars } from "@/types/api/user"

const key = "pages.user.remove"

export default mixins(socialsMixin).extend({
  props: {
    variables: { type: Object as VuePropType<DeletionRequestVars>, required: true },
    opened: { type: Boolean, required: true },
  },

  data: () => ({
    step: 1,
    loading: false,
  }),

  computed: {
    reasons (): Record<string, DeletionReason> {
      const result: Record<string, DeletionReason> = {}
      result[this.$t(`${key}.reason.notInteresting`) as string] = "NOT_INTERESTED"
      result[this.$t(`${key}.reason.dontUse`) as string] = "DONT_USE_ACCOUNT"
      result[this.$t(`${key}.reason.notifications`) as string] = "TOO_MANY_MAILINGS"
      result[this.$t(`${key}.reason.anotherAcc`) as string] = "OTHER_ACCOUNT"
      result[this.$t(`${key}.reason.secret`) as string] = "SECRET"
      result[this.$t(`${key}.reason.other`) as string] = "OTHER"
      return result
    },

    title (): string {
      switch (this.step) {
        case 1:
        case 4:
          return this.$t(`${key}.title.main`) as string
        case 2: return this.$t(`${key}.title.beforeLeave`) as string
        case 3:
          switch (this.variables.deleteReason) {
            case "NOT_INTERESTED": return this.$t(`${key}.title.together`) as string
            case "DONT_USE_ACCOUNT": return this.$t(`${key}.title.leave`) as string
            case "TOO_MANY_MAILINGS": return this.$t(`${key}.title.choice`) as string
            case "OTHER_ACCOUNT": return this.$t(`${key}.title.union`) as string
            case "SECRET": return this.$t(`${key}.title.respect`) as string
            case "OTHER": return this.$t(`${key}.title.improve`) as string
            default: return ""
          }
        case 5: return this.$t(`${key}.title.farewell`) as string
        default: return ""
      }
    },

    text (): string {
      switch (this.step) {
        case 1: return this.$t(`${key}.text.irreversibility`) as string
        case 2: return this.$t(`${key}.text.why`) as string
        case 3:
          switch (this.variables.deleteReason) {
            case "NOT_INTERESTED": return this.$t(`${key}.text.updating`) as string
            case "DONT_USE_ACCOUNT": return this.$t(`${key}.text.process`) as string
            case "TOO_MANY_MAILINGS": return this.$t(`${key}.text.unsubscribe`) as string
            case "OTHER_ACCOUNT": return this.$t(`${key}.text.union`) as string
            case "SECRET": return this.$t(`${key}.text.comeBack`) as string
            case "OTHER": return this.$t(`${key}.text.feedback`) as string
            default: return ""
          }
        case 4: return ""
        case 5: return this.$t(`${key}.text.threeDays`) as string
        case 6: return this.$t(`${key}.text.accepted`) as string
        default: return ""
      }
    },

    firstBtnText (): string {
      switch (this.step) {
        case 1: return this.$t(`${key}.label.saveAcc`) as string
        case 2: return this.$t("cancel") as string
        case 3:
          switch (this.variables.deleteReason) {
            case "NOT_INTERESTED": return this.$t(`${key}.label.follow`) as string
            case "DONT_USE_ACCOUNT":
            case "SECRET":
              return this.$t("cancel") as string
            case "TOO_MANY_MAILINGS": return this.$t(`${key}.label.unsubscribe`) as string
            case "OTHER_ACCOUNT": return this.$t(`${key}.label.sendId`) as string
            case "OTHER": return this.$t(`${key}.label.careService`) as string
            default: return ""
          }
        case 4: return this.$t("button.send") as string
        case 5: return this.$t(`${key}.label.changeMind`) as string
        case 6: return this.$t("button.close") as string
        default: return ""
      }
    },

    secondBtnText (): string {
      switch (this.step) {
        case 1: return this.$t(`${key}.label.continue`) as string
        case 2: return this.$t("button.send") as string
        case 3:
          switch (this.variables.deleteReason) {
            case "OTHER_ACCOUNT": return this.$t(`${key}.label.dontMerge`) as string
            case "OTHER": return this.$t(`${key}.label.feedback`) as string
            default: return this.$t(`${key}.label.remove`) as string
          }
        case 5: return this.$t(`${key}.label.remove`) as string
        default: return ""
      }
    },

    firstBtnDisabled (): boolean {
      return this.step === 4 && !this.variables.otherReason
    },

    secondBtnDisabled (): boolean {
      return this.step === 2 && !this.variables.deleteReason
    },
  },

  watch: {
    step (val) {
      this.variables.step = val
    },

    opened (val) {
      if (!val) this.reset()
    },
  },

  methods: {
    ...mapActions("user", { setAccDeletion: "SET_ACCOUNT_DELETION" }),

    async firstBtnClick () {
      switch (this.step) {
        case 3:
          switch (this.variables.deleteReason) {
            case "TOO_MANY_MAILINGS":
              await this.$api.user
                .setUserNotificationConsent(false)
              break
            case "OTHER_ACCOUNT":
              setTimeout(() => {
                this.$emit("input-id")
              }, 500)
              break
            case "OTHER":
              setTimeout(() => {
                this.$emit("open-feedback")
              }, 500)
              break
          }
          break
        case 4:
          this.step += 2
          return
        case 6:
          await this.createDeleteRequest()
      }

      this.$emit("close")
      this.step = 1
      this.variables.deleteReason = "NOT_INTERESTED"
    },

    secondBtnClick () {
      if (this.step === 3 && this.variables.deleteReason !== "OTHER") this.step += 2
      else this.step++
    },

    reset () {
      const v = this.variables
      this.step = 1
      v.step = undefined
      v.deleteReason = "DONT_USE_ACCOUNT"
      v.otherReason = undefined
      v.secondUser = undefined
    },

    async createDeleteRequest () {
      this.loading = true
      const [res, err] = await this.$api.user
        .createUserDeleteRequest(this.variables)

      if (err || !res) {
        console.error(err || this.$t("unknownError"))
        this.loading = false
        return
      }

      this.loading = false
      this.setAccDeletion(res)
    },
  },
})
